import React, { useState, useCallback, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import { compareAsc, format } from 'date-fns';
import {
  makeStyles,
  Theme,
  IconButton,
  Dialog,
  useMediaQuery,
  useTheme,
  Typography,
  Grid,
  Checkbox,
  Hidden,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { usePreference } from 'hooks';

const useStyles = makeStyles((theme: Theme) => ({
  dialog: {
    '& *': {
      color: '#ffffff',
    },
    minWidth: '320px',
  },
  paper: {
    background: 'transparent',
    borderRadius: 0,
    boxShadow: 'none',
    width: '100vh',
    [theme.breakpoints.down('md')]: {
      width: '100%',
      padding: '0 40px 0 40px',
    },
  },
  backdrop: {},
  closeButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  closeButton: {
    padding: '5px',
  },
  closeIcon: {
    fontSize: '35px',
    [theme.breakpoints.down('md')]: {
      fontSize: '40px',
    },
  },
  body: {
    '& > img': {
      width: '100%',
    },
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    '& > p': {
      fontSize: '20px',
    },
  },
  checkbox: {
    padding: 0,
    marginLeft: '10px',
  },
  label: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
}));

function BannerModal() {
  const classes = useStyles();

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const HAS_CHECKED_TODAY: string | null = localStorage.getItem('hasCheckedToday');

  const { webBannerFilePath, mobileBannerFilePath, useBanner } = usePreference();

  const [showBanner, setShowBanner] = useState<boolean>(false);
  const [checkedBanner, setCheckedBanner] = useState<boolean>(false);

  useEffect(() => {
    const toNumberDateOfToday = Number(format(new Date(), 'yyyyMMdd'));
    if (useBanner && !HAS_CHECKED_TODAY) {
      setShowBanner(true);
    } else if (useBanner && HAS_CHECKED_TODAY && Number(HAS_CHECKED_TODAY) < toNumberDateOfToday) {
      setShowBanner(true);
    }
  }, [useBanner]);

  const onCloseModal = useCallback(() => {
    setShowBanner(false);
    if (checkedBanner) {
      const today = new Date();
      const toStringDate = format(today, 'yyyyMMdd');
      localStorage.setItem('hasCheckedToday', toStringDate);
    }
  }, [checkedBanner]);

  const onChangeCheck = useCallback(() => {
    setCheckedBanner(!checkedBanner);
  }, [checkedBanner]);

  const onErrorMobileImage = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
      event.currentTarget.src = webBannerFilePath;
    },
    [webBannerFilePath],
  );

  return (
    <Dialog
      fullScreen={fullScreen}
      open={showBanner}
      onClose={onCloseModal}
      className={classes.dialog}
      PaperProps={{
        className: clsx(classes.paper),
      }}
      BackdropProps={{
        className: clsx(classes.backdrop),
      }}
      maxWidth="lg"
      scroll="body"
    >
      <div className={classes.closeButtonContainer}>
        <IconButton onClick={onCloseModal} className={classes.closeButton}>
          <CloseIcon className={classes.closeIcon} />
        </IconButton>
      </div>
      <section className={classes.body}>
        <Hidden mdDown>
          <img src={webBannerFilePath} alt="이미지가 존재하지 않습니다." />
        </Hidden>
        <Hidden lgUp>
          <img
            src={mobileBannerFilePath}
            alt="이미지가 존재하지 않습니다."
            onError={onErrorMobileImage}
          />
        </Hidden>
      </section>
      <footer className={classes.footer}>
        <label htmlFor="hasCheckedToday" className={classes.label}>
          <Typography>오늘 하루 열지 않기</Typography>
        </label>
        <Checkbox
          className={classes.checkbox}
          color="primary"
          inputProps={{ 'aria-label': 'banner', id: 'hasCheckedToday' }}
          value={checkedBanner}
          onChange={onChangeCheck}
        />
      </footer>
    </Dialog>
  );
}

export default BannerModal;
